// Resolves "who has the kids on date X" for a repeating N-week parenting rotation,
// optionally overridden by holiday-specific rules from the Holiday Schedule.
//
// Data model:
//
//   rotation = {
//     anchorDate: '2026-01-04',        // Sunday 00:00 local time. Day 0 of the cycle.
//     weeks: [
//       {
//         events: [
//           { partner: 'A', startDay: 'mon', startTime: '14:00', endDay: 'wed', endTime: '14:00' },
//           { partner: 'B', startDay: 'wed', startTime: '14:00', endDay: 'fri', endTime: '14:00' },
//           { partner: 'A', startDay: 'fri', startTime: '14:00', endDay: 'mon', endTime: '14:00' },  // wraps into next week
//         ]
//       },
//       { events: [ … ] },                // Week 2 — inverse of week 1, etc.
//       // …up to any number of weeks (2, 4, 7, …)
//     ]
//   }
//
// Days are 'sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat' (any case, or full names).
// Times are 'HH:MM' 24-hour, or '00:00' for start-of-day.
// An event whose endDay×endTime is BEFORE startDay×startTime in the week is
// interpreted as wrapping into the next week within the cycle (e.g. Fri→Mon).

import { parentForDate } from './proseRenderer.js'
import { holidaysCoveringDate, resolveHolidayDate } from './dates.js'
import { MIN_PER_DAY, MIN_PER_WEEK, timeToMin } from './weekMath.js'

// --- Day-name helpers ----------------------------------------------------
//
// Days are Monday-anchored: mon=0, tue=1, …, sun=6. This matches the
// human "weekly pattern" mental model (weekdays first, weekend last) and
// makes ranges like "Fri → Sun" natural — Sun is the LAST day of the
// week, chronologically after Fri, not before it. Anchors on the schedule
// document must accordingly be a Monday.

// Re-exported so existing `from './rotation.js'` imports keep working.
export { DAYS_OF_WEEK } from './weekMath.js'

// A lenient parser for free-text day names, not a duplicate of weekMath's
// DAY_INDEX: it accepts abbreviations and throws on anything it cannot read.
const DAY_NAME_INDEX = {
  mon: 0, monday: 0, mo: 0, m: 0,
  tue: 1, tuesday: 1, tu: 1, tues: 1,
  wed: 2, wednesday: 2, we: 2,
  thu: 3, thursday: 3, th: 3, thur: 3, thurs: 3,
  fri: 4, friday: 4, fr: 4, f: 4,
  sat: 5, saturday: 5, sa: 5,
  sun: 6, sunday: 6, su: 6,
}

function dayIndex(name) {
  if (typeof name === 'number') return name
  const key = String(name || '').toLowerCase().trim()
  const idx = DAY_NAME_INDEX[key]
  if (idx === undefined) throw new Error(`Unknown day name: ${name}`)
  return idx
}


// --- Core: resolve rotation only (no holiday overlay) --------------------

// Given a specific Date and a rotation config, returns 'A' | 'B' | null.
// null means: the rotation has no event covering this moment (a gap).
export function resolveRotationParent(date, rotation) {
  if (!rotation || !rotation.weeks || rotation.weeks.length === 0) return null

  const anchor = parseAnchor(rotation.anchorDate)
  const cycleMins = rotation.weeks.length * MIN_PER_WEEK

  // Signed minute offset from anchor, in WALL-CLOCK terms: whole calendar
  // days plus minutes-within-day. Computing elapsed real time instead would
  // shift every handoff by an hour after a DST change — a "2pm handoff"
  // must mean 2pm local, year-round. Math.round absorbs the ±1h that DST
  // adds to the midnight-to-midnight difference.
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const dayDiff = Math.round((dateMidnight.getTime() - anchor.getTime()) / 86_400_000)
  const deltaMins = dayDiff * MIN_PER_DAY + date.getHours() * 60 + date.getMinutes()
  const q = ((deltaMins % cycleMins) + cycleMins) % cycleMins  // 0 ≤ q < cycleMins

  for (let w = 0; w < rotation.weeks.length; w++) {
    const week = rotation.weeks[w]
    if (!week || !week.events) continue
    for (const event of week.events) {
      // startWeekOffset / endWeekOffset are 0 for events that start/end in
      // the event's home week (`w`), or 1 for events whose start or end is
      // in the following week. Introduced explicitly so the UI can label
      // "This week" vs "Next week" without inferring from day ordering.
      // Legacy events (no offset fields) fall back to implicit wrap.
      const startWeekOffset = event.startWeekOffset || 0
      const endWeekOffset = event.endWeekOffset || 0
      const start = (w + startWeekOffset) * MIN_PER_WEEK
        + dayIndex(event.startDay) * MIN_PER_DAY
        + timeToMin(event.startTime)
      let end = (w + endWeekOffset) * MIN_PER_WEEK
        + dayIndex(event.endDay) * MIN_PER_DAY
        + timeToMin(event.endTime)
      // Safety: if the offsets don't disambiguate (e.g. same-day/time),
      // wrap forward one week so a "Mon 00:00 → Mon 00:00" event covers
      // a full week rather than collapsing to zero minutes.
      if (end <= start) end += MIN_PER_WEEK
      // end may now exceed cycleMins — if it does, the event straddles the cycle boundary
      if (end > cycleMins) {
        const wrappedEnd = end - cycleMins
        if (q >= start || q < wrappedEnd) return event.partner
      } else {
        if (q >= start && q < end) return event.partner
      }
    }
  }
  return null
}

// --- Full stack: rotation + holiday overlay ------------------------------

// The main entry point when a calendar view needs to paint a day:
//   resolveParentForDate(date, rotation, holidays)
//
// Steps:
//   1. Find a holiday whose configured dates cover `date`.
//   2. If found, ask parentForYear(rule, year):
//        'A' | 'B' | 'shared' | 'split'  → holiday wins, return it
//        'base' (no-change)              → fall through to rotation
//        'flex' (suspend-flex)           → fall through to rotation
//        null / 'rotation'                → fall through to rotation
//   3. Otherwise: return resolveRotationParent(date, rotation).
//
// Return: 'A' | 'B' | 'shared' | 'split' | null.
export function resolveParentForDate(date, rotation, holidays, opts = {}) {
  const hits = holidays && holidays.length
    ? holidaysCoveringDate(date, holidays, { jurisdiction: opts.jurisdiction })
    : []

  // Narrowest covering holiday first; walk outward until one yields a
  // concrete outcome. An undecided Christmas Day inside a decided Winter
  // Break defers to the break, not to the weekly rotation.
  for (const hit of hits) {
    if (!hit.holiday.rule) continue
    const who = parentForDate(hit.holiday.rule, date, hit)
    if (who === 'A' || who === 'B' || who === 'shared' || who === 'split') return who
    // 'base', 'flex', 'rotation', null → try the next covering holiday
  }
  return resolveRotationParent(date, rotation)
}

// --- Internal ------------------------------------------------------------

// Parse anchor to a Date at local midnight. Accepts 'YYYY-MM-DD' or Date.
function parseAnchor(anchor) {
  if (!anchor) throw new Error('rotation.anchorDate is required')
  if (anchor instanceof Date) {
    return new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate())
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(anchor))
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(anchor)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}
